import {
  ClampToEdgeWrapping, HalfFloatType, LinearFilter, NoBlending, RGBAFormat, ShaderMaterial, Vector2,
  WebGLRenderTarget,
} from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';

// Fluid 2D incompressible ("stable fluids", Stam) sobre una graella fixa, independent del nombre de
// partícules. Velocitat en CEL·LES/segon. Per frame: splat del cursor → curl → vorticity confinement
// → divergència → pressió (Jacobi) → projecció → advecció de velocitat i de tinta.
// La incompressibilitat és el que genera desviació lateral i vòrtexs; l'advecció és el que fa que
// la pertorbació viatgi quan el cursor ja ha passat.

const VERT = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const SEGMENT = `
  uniform vec2 uA;
  uniform vec2 uB;
  uniform float uRadius;
  float splatFalloff(vec2 p) {
    vec2 pa = p - uA;
    vec2 ba = uB - uA;
    float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-10), 0.0, 1.0);
    float d = length(pa - ba * h);
    return exp(-d * d / (uRadius * uRadius));
  }
`;

const SPLAT_VELOCITY = `
  uniform sampler2D uVelocity;
  uniform vec2 uTarget;
  uniform float uStrength;
  varying vec2 vUv;
  ${SEGMENT}
  void main() {
    vec2 v = texture2D(uVelocity, vUv).xy;
    // Relaxació PARCIAL cap a la velocitat del cursor (no suma): la velocitat local s'hi apropa en
    // unes dècimes de segon, així que un canvi brusc de direcció xoca amb el moment existent.
    v = mix(v, uTarget, clamp(splatFalloff(vUv) * uStrength, 0.0, 1.0));
    gl_FragColor = vec4(v, 0.0, 1.0);
  }
`;

const SPLAT_DYE = `
  uniform sampler2D uDye;
  uniform float uAmount;
  varying vec2 vUv;
  ${SEGMENT}
  void main() {
    float dye = texture2D(uDye, vUv).x;
    dye = max(dye, splatFalloff(vUv) * uAmount);
    gl_FragColor = vec4(dye, 0.0, 0.0, 1.0);
  }
`;

const CURL = `
  uniform sampler2D uVelocity;
  uniform vec2 uTexel;
  varying vec2 vUv;
  void main() {
    float L = texture2D(uVelocity, vUv - vec2(uTexel.x, 0.0)).y;
    float R = texture2D(uVelocity, vUv + vec2(uTexel.x, 0.0)).y;
    float T = texture2D(uVelocity, vUv + vec2(0.0, uTexel.y)).x;
    float B = texture2D(uVelocity, vUv - vec2(0.0, uTexel.y)).x;
    gl_FragColor = vec4(0.5 * (R - L - T + B), 0.0, 0.0, 1.0);
  }
`;

const VORTICITY = `
  uniform sampler2D uVelocity;
  uniform sampler2D uCurl;
  uniform vec2 uTexel;
  uniform float uCurlStrength;
  uniform float uDt;
  varying vec2 vUv;
  void main() {
    float L = texture2D(uCurl, vUv - vec2(uTexel.x, 0.0)).x;
    float R = texture2D(uCurl, vUv + vec2(uTexel.x, 0.0)).x;
    float T = texture2D(uCurl, vUv + vec2(0.0, uTexel.y)).x;
    float B = texture2D(uCurl, vUv - vec2(0.0, uTexel.y)).x;
    float C = texture2D(uCurl, vUv).x;
    vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));
    force /= length(force) + 1e-4;
    force *= uCurlStrength * C;
    force.y *= -1.0;
    vec2 v = texture2D(uVelocity, vUv).xy + force * uDt;
    gl_FragColor = vec4(v, 0.0, 1.0);
  }
`;

const DIVERGENCE = `
  uniform sampler2D uVelocity;
  uniform vec2 uTexel;
  varying vec2 vUv;
  void main() {
    vec2 C = texture2D(uVelocity, vUv).xy;
    float L = texture2D(uVelocity, vUv - vec2(uTexel.x, 0.0)).x;
    float R = texture2D(uVelocity, vUv + vec2(uTexel.x, 0.0)).x;
    float T = texture2D(uVelocity, vUv + vec2(0.0, uTexel.y)).y;
    float B = texture2D(uVelocity, vUv - vec2(0.0, uTexel.y)).y;
    if (vUv.x - uTexel.x < 0.0) L = -C.x;
    if (vUv.x + uTexel.x > 1.0) R = -C.x;
    if (vUv.y + uTexel.y > 1.0) T = -C.y;
    if (vUv.y - uTexel.y < 0.0) B = -C.y;
    gl_FragColor = vec4(0.5 * (R - L + T - B), 0.0, 0.0, 1.0);
  }
`;

const SCALE = `
  uniform sampler2D uSource;
  uniform float uValue;
  varying vec2 vUv;
  void main() {
    gl_FragColor = texture2D(uSource, vUv) * uValue;
  }
`;

const PRESSURE = `
  uniform sampler2D uPressure;
  uniform sampler2D uDivergence;
  uniform vec2 uTexel;
  varying vec2 vUv;
  void main() {
    float L = texture2D(uPressure, vUv - vec2(uTexel.x, 0.0)).x;
    float R = texture2D(uPressure, vUv + vec2(uTexel.x, 0.0)).x;
    float T = texture2D(uPressure, vUv + vec2(0.0, uTexel.y)).x;
    float B = texture2D(uPressure, vUv - vec2(0.0, uTexel.y)).x;
    float div = texture2D(uDivergence, vUv).x;
    gl_FragColor = vec4((L + R + B + T - div) * 0.25, 0.0, 0.0, 1.0);
  }
`;

const GRADIENT_SUBTRACT = `
  uniform sampler2D uPressure;
  uniform sampler2D uVelocity;
  uniform vec2 uTexel;
  varying vec2 vUv;
  void main() {
    float L = texture2D(uPressure, vUv - vec2(uTexel.x, 0.0)).x;
    float R = texture2D(uPressure, vUv + vec2(uTexel.x, 0.0)).x;
    float T = texture2D(uPressure, vUv + vec2(0.0, uTexel.y)).x;
    float B = texture2D(uPressure, vUv - vec2(0.0, uTexel.y)).x;
    vec2 v = texture2D(uVelocity, vUv).xy - 0.5 * vec2(R - L, T - B);
    gl_FragColor = vec4(v, 0.0, 1.0);
  }
`;

const ADVECT = `
  uniform sampler2D uVelocity;
  uniform sampler2D uSource;
  uniform vec2 uTexel;
  uniform float uDt;
  uniform float uDissipation;
  varying vec2 vUv;
  void main() {
    vec2 coord = vUv - uDt * texture2D(uVelocity, vUv).xy * uTexel;
    gl_FragColor = texture2D(uSource, coord) / (1.0 + uDissipation * uDt);
  }
`;

// Arrossegar la pressió del frame anterior (× aquest factor) fa convergir el Jacobi amb menys iteracions.
const PRESSURE_CARRY = 0.8;

function material(fragmentShader, uniforms) {
  return new ShaderMaterial({
    vertexShader: VERT,
    fragmentShader,
    uniforms,
    depthTest: false,
    depthWrite: false,
    blending: NoBlending,
  });
}

class DoubleTarget {
  constructor(make) {
    this.read = make();
    this.write = make();
  }

  swap() {
    const t = this.read;
    this.read = this.write;
    this.write = t;
  }

  dispose() {
    this.read.dispose();
    this.write.dispose();
  }
}

export default class FluidField {
  constructor(renderer, resolution) {
    this.renderer = renderer;
    this.resolution = resolution;

    const make = () => new WebGLRenderTarget(resolution, resolution, {
      type: HalfFloatType,
      format: RGBAFormat,
      minFilter: LinearFilter,
      magFilter: LinearFilter,
      wrapS: ClampToEdgeWrapping,
      wrapT: ClampToEdgeWrapping,
      depthBuffer: false,
      stencilBuffer: false,
    });

    this._velocity = new DoubleTarget(make);
    this._dye = new DoubleTarget(make);
    this._pressure = new DoubleTarget(make);
    this._curl = make();
    this._divergence = make();

    const texel = new Vector2(1 / resolution, 1 / resolution);
    const segment = () => ({ uA: { value: new Vector2() }, uB: { value: new Vector2() }, uRadius: { value: 0.05 } });

    this._m = {
      splatVelocity: material(SPLAT_VELOCITY, {
        ...segment(), uVelocity: { value: null }, uTarget: { value: new Vector2() }, uStrength: { value: 0 },
      }),
      splatDye: material(SPLAT_DYE, { ...segment(), uDye: { value: null }, uAmount: { value: 0 } }),
      curl: material(CURL, { uVelocity: { value: null }, uTexel: { value: texel } }),
      vorticity: material(VORTICITY, {
        uVelocity: { value: null }, uCurl: { value: null }, uTexel: { value: texel },
        uCurlStrength: { value: 0 }, uDt: { value: 0 },
      }),
      divergence: material(DIVERGENCE, { uVelocity: { value: null }, uTexel: { value: texel } }),
      scale: material(SCALE, { uSource: { value: null }, uValue: { value: PRESSURE_CARRY } }),
      pressure: material(PRESSURE, {
        uPressure: { value: null }, uDivergence: { value: null }, uTexel: { value: texel },
      }),
      gradientSubtract: material(GRADIENT_SUBTRACT, {
        uPressure: { value: null }, uVelocity: { value: null }, uTexel: { value: texel },
      }),
      advect: material(ADVECT, {
        uVelocity: { value: null }, uSource: { value: null }, uTexel: { value: texel },
        uDt: { value: 0 }, uDissipation: { value: 0 },
      }),
    };

    this._quad = new FullScreenQuad(null);
  }

  get velocityTexture() { return this._velocity.read.texture; }

  get dyeTexture() { return this._dye.read.texture; }

  _pass(mat, target) {
    this._quad.material = mat;
    this.renderer.setRenderTarget(target);
    this._quad.render(this.renderer);
  }

  _setSegment(u, p) {
    u.uA.value.set(p.fromX, p.fromY);
    u.uB.value.set(p.toX, p.toY);
    u.uRadius.value = p.radius;
  }

  // p: { fromX, fromY, toX, toY, radius (uv) — segment del cursor; targetX, targetY (cel·les/s);
  //      strength (0..1, pes del splat de velocitat); ink (0..1); vorticity; pressureIterations;
  //      velocityDissipation; inkDissipation }
  step(dt, p) {
    const m = this._m;
    const prevTarget = this.renderer.getRenderTarget();

    if (p.strength > 0) {
      const u = m.splatVelocity.uniforms;
      this._setSegment(u, p);
      u.uVelocity.value = this._velocity.read.texture;
      u.uTarget.value.set(p.targetX, p.targetY);
      u.uStrength.value = p.strength;
      this._pass(m.splatVelocity, this._velocity.write);
      this._velocity.swap();
    }

    if (p.ink > 0) {
      const u = m.splatDye.uniforms;
      this._setSegment(u, p);
      u.uDye.value = this._dye.read.texture;
      u.uAmount.value = p.ink;
      this._pass(m.splatDye, this._dye.write);
      this._dye.swap();
    }

    m.curl.uniforms.uVelocity.value = this._velocity.read.texture;
    this._pass(m.curl, this._curl);

    const vu = m.vorticity.uniforms;
    vu.uVelocity.value = this._velocity.read.texture;
    vu.uCurl.value = this._curl.texture;
    vu.uCurlStrength.value = p.vorticity;
    vu.uDt.value = dt;
    this._pass(m.vorticity, this._velocity.write);
    this._velocity.swap();

    m.divergence.uniforms.uVelocity.value = this._velocity.read.texture;
    this._pass(m.divergence, this._divergence);

    m.scale.uniforms.uSource.value = this._pressure.read.texture;
    this._pass(m.scale, this._pressure.write);
    this._pressure.swap();

    const pu = m.pressure.uniforms;
    pu.uDivergence.value = this._divergence.texture;
    const iterations = Math.max(1, Math.round(p.pressureIterations));
    for (let i = 0; i < iterations; i++) {
      pu.uPressure.value = this._pressure.read.texture;
      this._pass(m.pressure, this._pressure.write);
      this._pressure.swap();
    }

    const gu = m.gradientSubtract.uniforms;
    gu.uPressure.value = this._pressure.read.texture;
    gu.uVelocity.value = this._velocity.read.texture;
    this._pass(m.gradientSubtract, this._velocity.write);
    this._velocity.swap();

    const au = m.advect.uniforms;
    au.uDt.value = dt;
    au.uVelocity.value = this._velocity.read.texture;
    au.uSource.value = this._velocity.read.texture;
    au.uDissipation.value = p.velocityDissipation;
    this._pass(m.advect, this._velocity.write);
    this._velocity.swap();

    au.uVelocity.value = this._velocity.read.texture;
    au.uSource.value = this._dye.read.texture;
    au.uDissipation.value = p.inkDissipation;
    this._pass(m.advect, this._dye.write);
    this._dye.swap();

    this.renderer.setRenderTarget(prevTarget);
  }

  dispose() {
    this._velocity.dispose();
    this._dye.dispose();
    this._pressure.dispose();
    this._curl.dispose();
    this._divergence.dispose();
    Object.values(this._m).forEach((mat) => mat.dispose());
    this._quad.dispose();
  }
}
